import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Seo from '../components/common/Seo';
import { getOrganizationSchema } from '../utils/structuredDataHelpers';
import {
    Box,
    Container,
    Typography,
    Button,
    Card,
    CardContent,
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    useTheme,
    useMediaQuery,
    Divider,
    IconButton,
    Stack,
    Fade,
    Slide
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    Search as SearchIcon,
    People as PeopleIcon,
    Security as SecurityIcon,
    AttachMoney as AttachMoneyIcon,
    Celebration as CelebrationIcon,
    Home as HomeIcon,
    East as EastIcon,
} from '@mui/icons-material';

function Home() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [expandedFaq, setExpandedFaq] = useState(false);
    const location = useLocation();
    const [scrollY, setScrollY] = useState(0);

    // パララックス効果のためのスクロール位置監視
    useEffect(() => {
        const handleScroll = () => {
            setScrollY(window.scrollY);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleFaqChange = (panel) => (event, isExpanded) => {
        setExpandedFaq(isExpanded ? panel : false);
    };

    // ホーム画面表示時に、ナビゲーションタブの選択状態をリセットする効果
    useEffect(() => {
        if (location.pathname === '/') {
            // カスタムイベントを発行してボトムナビゲーションの選択状態をリセット
            const resetNavEvent = new CustomEvent('resetNavigation');
            window.dispatchEvent(resetNavEvent);
        }
    }, [location.pathname]);

    return (
        <>
            <Seo
                title="ルームシェア・ルームメイトを探せるマッチングプラットフォーム・掲示板 | Room Share Matching"
                description="日本全国のルームシェア物件・ルームメイトを探せるマッチングサービス。安心安全なルームシェア探しをサポートします。"
                path="/"
                keywords="ルームシェア,ルームメイト,マッチング,賃貸,部屋探し,同居人"
                schema={getOrganizationSchema()}
            />

            {/* ヒーローセクション - シンプルなデザイン */}
            <Box
                sx={{
                    position: 'relative',
                    pt: { xs: 10, sm: 14 },
                    pb: { xs: 8, sm: 10 },
                    borderBottom: `1px solid ${theme.palette.grey[100]}`,
                    overflow: 'hidden',
                    background: `linear-gradient(135deg, #6a89cc, #79a8ed)`,
                }}
            >
                {/* グラデーションバックグラウンド - モダンなデザイン */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 0,
                        background: `radial-gradient(circle at 30% 70%, #b8daffa0, transparent 50%),
                                    radial-gradient(circle at 70% 30%, #e3f0ff80, transparent 50%)`,
                    }}
                />

                {/* モダンな装飾要素 - グリッドパターン */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 0,
                        opacity: 0.05,
                        backgroundImage: `
                            linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
                        `,
                        backgroundSize: '20px 20px',
                    }}
                />

                {/* 波形装飾 - 下部 */}
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: -2,
                        left: 0,
                        right: 0,
                        height: '100px',
                        zIndex: 2,
                        background: '#fff',
                        clipPath: 'polygon(0% 100%, 100% 100%, 100% 40%, 75% 50%, 50% 35%, 25% 50%, 0% 40%)',
                    }}
                />

                <Container maxWidth="md" sx={{ position: 'relative', zIndex: 3 }}>
                    <Fade in={true} timeout={1000}>
                        <Box
                            sx={{
                                textAlign: 'center',
                                mb: 4,
                            }}
                        >
                            <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                <Typography
                                    variant="h2"
                                    component="h1"
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: { xs: '2.8rem', sm: '3.8rem' },
                                        mb: 1,
                                        color: '#fff',
                                        textShadow: '0 2px 10px rgba(0,0,0,0.1)',
                                        position: 'relative',
                                        letterSpacing: '0.02em',
                                        '&::after': {
                                            content: '""',
                                            position: 'absolute',
                                            bottom: -8,
                                            left: '25%',
                                            width: '50%',
                                            height: '4px',
                                            background: 'linear-gradient(90deg, rgba(255,255,255,0.2), rgba(255,255,255,1), rgba(255,255,255,0.2))',
                                            borderRadius: '4px',
                                        }
                                    }}
                                >
                                    ともに暮らし、豊かに
                                </Typography>
                            </Box>
                            <Typography
                                variant="h5"
                                component="h2"
                                sx={{
                                    fontWeight: 400,
                                    mt: 3,
                                    mb: 5,
                                    fontSize: { xs: '1.3rem', sm: '1.6rem' },
                                    color: 'rgba(255,255,255,0.9)',
                                    maxWidth: '800px',
                                    mx: 'auto',
                                    lineHeight: 1.6,
                                    letterSpacing: '0.01em',
                                    position: 'relative',
                                    '&::before, &::after': {
                                        content: '""',
                                        position: 'absolute',
                                        width: '40px',
                                        height: '1px',
                                        background: 'rgba(255,255,255,0.5)',
                                        top: '50%',
                                        display: { xs: 'none', md: 'block' }
                                    },
                                    '&::before': {
                                        left: -60
                                    },
                                    '&::after': {
                                        right: -60
                                    }
                                }}
                            >
                                理想のルームシェア物件やルームシェア相手が見つかる、安心のマッチングサービス
                            </Typography>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 3,
                                mt: 2,
                                position: 'relative',
                                zIndex: 3
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    justifyContent: 'center',
                                    gap: { xs: 2, sm: 3 },
                                }}>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Button
                                            variant="contained"
                                            size="large"
                                            onClick={() => navigate('/room-search')}
                                            sx={{
                                                borderRadius: '30px',
                                                px: { xs: 3, sm: 4 },
                                                py: 1.8,
                                                fontSize: { xs: '1rem', sm: '1.15rem' },
                                                fontWeight: 600,
                                                boxShadow: '0 10px 20px rgba(0,0,0,0.15)',
                                                background: '#fff',
                                                color: '#7e9ecc',
                                                transition: 'all 0.3s ease',
                                                position: 'relative',
                                                overflow: 'hidden',
                                                '&::after': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: '-100%',
                                                    width: '100%',
                                                    height: '100%',
                                                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                                                    animation: 'heroButtonShine 3s infinite',
                                                },
                                                '@keyframes heroButtonShine': {
                                                    '0%': { left: '-100%' },
                                                    '20%': { left: '100%' },
                                                    '100%': { left: '100%' },
                                                },
                                                '&:hover': {
                                                    boxShadow: '0 15px 25px rgba(0,0,0,0.2)',
                                                    transform: 'translateY(-3px)',
                                                    background: '#fff',
                                                }
                                            }}
                                        >
                                            住める物件を探す
                                        </Button>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                display: 'block',
                                                mt: 1,
                                                color: 'rgba(255,255,255,0.8)',
                                                fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                                fontWeight: 400
                                            }}
                                        >
                                            すぐ住める部屋を見つける
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Button
                                            variant="outlined"
                                            size="large"
                                            onClick={() => navigate('/renters')}
                                            sx={{
                                                borderRadius: '30px',
                                                px: { xs: 3, sm: 4 },
                                                py: 1.8,
                                                fontSize: { xs: '1rem', sm: '1.15rem' },
                                                fontWeight: 600,
                                                borderWidth: 2,
                                                borderColor: 'rgba(255,255,255,0.8)',
                                                color: '#fff',
                                                transition: 'all 0.3s ease',
                                                backdropFilter: 'blur(5px)',
                                                '&:hover': {
                                                    borderWidth: 2,
                                                    borderColor: '#fff',
                                                    background: 'rgba(255,255,255,0.1)',
                                                    transform: 'translateY(-3px)',
                                                    boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                                                }
                                            }}
                                        >
                                            一緒に住む人を探す
                                        </Button>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                display: 'block',
                                                mt: 1,
                                                color: 'rgba(255,255,255,0.8)',
                                                fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                                fontWeight: 400
                                            }}
                                        >
                                            物件と相手を一緒に探す
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* オーナー向け CTA */}
                                <Box sx={{ textAlign: 'center' }}>
                                    <Button
                                        variant="outlined"
                                        size="large"
                                        onClick={() => navigate(currentUser ? '/create-listing' : '/register')}
                                        sx={{
                                            borderRadius: '30px',
                                            px: { xs: 3, sm: 4 },
                                            py: 1.8,
                                            fontSize: { xs: '1rem', sm: '1.15rem' },
                                            fontWeight: 600,
                                            borderWidth: 2,
                                            borderColor: 'rgba(255,255,255,0.8)',
                                            color: '#fff',
                                            transition: 'all 0.3s ease',
                                            backdropFilter: 'blur(5px)',
                                            '&:hover': {
                                                borderWidth: 2,
                                                borderColor: '#fff',
                                                background: 'rgba(255,255,255,0.1)',
                                                transform: 'translateY(-3px)',
                                                boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                                            }
                                        }}
                                    >
                                        部屋を貸したい方
                                    </Button>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            display: 'block',
                                            mt: 1,
                                            color: 'rgba(255,255,255,0.8)',
                                            fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                            fontWeight: 400
                                        }}
                                    >
                                        無料で物件を掲載する
                                    </Typography>
                                </Box>

                                {/* 登録促進の軽いCTA */}
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'rgba(255,255,255,0.75)',
                                        fontSize: { xs: '0.8rem', sm: '0.85rem' },
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            color: 'rgba(255,255,255,0.95)',
                                            transform: 'translateY(-1px)',
                                        }
                                    }}
                                    onClick={() => navigate('/register')}
                                >
                                    まずは無料で登録してみる（3分で完了）
                                </Typography>
                            </Box>

                            {/* スクロールプロンプト */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: -60,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    animation: 'bounce 2s infinite',
                                    opacity: 0.8,
                                    '@keyframes bounce': {
                                        '0%, 20%, 50%, 80%, 100%': { transform: 'translateX(-50%) translateY(0)' },
                                        '40%': { transform: 'translateX(-50%) translateY(-10px)' },
                                        '60%': { transform: 'translateX(-50%) translateY(-5px)' },
                                    }
                                }}
                            >
                                <Box sx={{
                                    width: '30px',
                                    height: '50px',
                                    border: '2px solid',
                                    borderColor: '#a5c6f7',
                                    borderRadius: '20px',
                                    position: 'relative',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: '8px',
                                        left: '50%',
                                        width: '6px',
                                        height: '6px',
                                        backgroundColor: '#a5c6f7',
                                        borderRadius: '50%',
                                        transform: 'translateX(-50%)',
                                        animation: 'scroll 2s infinite',
                                    },
                                    '@keyframes scroll': {
                                        '0%': { top: '8px', opacity: 1 },
                                        '100%': { top: '32px', opacity: 0 }
                                    }
                                }} />
                            </Box>
                        </Box>
                    </Fade>
                </Container>
            </Box>

            {/* サービスコンセプト - 洗練されたデザイン */}
            <Box
                id="concept"
                sx={{
                    py: { xs: 8, sm: 12 },
                    background: '#fff',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* 装飾要素 - 波線パターン */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        bottom: 0,
                        width: '60%',
                        opacity: 0.04,
                        backgroundImage: `
                            repeating-linear-gradient(
                                45deg,
                                #7e9ecc,
                                #7e9ecc 2px,
                                transparent 2px,
                                transparent 10px
                            )
                        `,
                    }}
                />

                {/* 装飾要素 - 円形 */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: '20%',
                        left: '-5%',
                        width: '200px',
                        height: '200px',
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, #b8daff20, #7e9ecc10)`,
                        zIndex: 0,
                    }}
                />

                <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ textAlign: 'center', maxWidth: '800px', mx: 'auto' }}>
                        <Typography
                            variant="overline"
                            component="div"
                            sx={{
                                color: '#7e9ecc',
                                fontWeight: 600,
                                mb: 1,
                                letterSpacing: 2,
                                fontSize: '0.9rem',
                            }}
                        >
                            ABOUT OUR SERVICE
                        </Typography>
                        <Typography
                            variant="h3"
                            component="h2"
                            sx={{
                                fontWeight: 700,
                                mb: 3,
                                fontSize: { xs: '2rem', sm: '2.4rem' },
                                background: `linear-gradient(135deg, #6a89cc, #79a8ed)`,
                                backgroundClip: 'text',
                                textFillColor: 'transparent',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                position: 'relative',
                                display: 'inline-block',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: -10,
                                    left: '50%',
                                    width: '60px',
                                    height: '4px',
                                    background: '#7e9ecc',
                                    borderRadius: '2px',
                                    transform: 'translateX(-50%)',
                                }
                            }}
                        >
                            Room Share Matchingとは
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                mb: 5,
                                fontSize: '1.1rem',
                                lineHeight: 1.8,
                                color: 'text.secondary',
                                mx: 'auto',
                            }}
                        >
                            ルームシェア物件を提供する方×ルームシェア希望者や、気の合うルームシェア相手を探している方同士をつなぐマッチングサービス。物件探しからルームメイト探しまで、新しい暮らしのスタートをサポートします。
                        </Typography>
                        <Button
                            variant="contained"
                            size="large"
                            endIcon={<EastIcon />}
                            onClick={() => navigate('/register')}
                            sx={{
                                borderRadius: '30px',
                                px: 4,
                                py: 1.5,
                                background: `linear-gradient(135deg, #79a8ed, #6a89cc)`,
                                transition: 'all 0.3s ease',
                                boxShadow: '0 6px 15px rgba(106, 137, 204, 0.2)',
                                '&:hover': {
                                    transform: 'translateY(-3px)',
                                    boxShadow: '0 10px 20px rgba(106, 137, 204, 0.3)',
                                }
                            }}
                        >
                            アカウント登録
                        </Button>
                    </Box>
                </Container>
            </Box>

            {/* 機能紹介 - 洗練されたデザイン */}
            <Box
                sx={{
                    py: { xs: 8, sm: 12 },
                    background: `linear-gradient(135deg, ${theme.palette.grey[50]}, ${theme.palette.grey[100]}50)`,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* 装飾要素 - 背景パターン */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        opacity: 0.03,
                        backgroundImage: `
                            radial-gradient(#7e9ecc 2px, transparent 2px),
                            radial-gradient(#7e9ecc 2px, transparent 2px)
                        `,
                        backgroundSize: '40px 40px',
                        backgroundPosition: '0 0, 20px 20px',
                    }}
                />

                {/* 波形装飾 - 上部 */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: -1,
                        left: 0,
                        right: 0,
                        height: '80px',
                        background: '#fff',
                        clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 75% 60%, 50% 100%, 25% 60%, 0% 100%)',
                    }}
                />

                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ textAlign: 'center', mb: 7 }}>
                        <Typography
                            variant="overline"
                            component="div"
                            sx={{
                                color: '#7e9ecc',
                                fontWeight: 600,
                                mb: 1,
                                letterSpacing: 2,
                                fontSize: '0.9rem',
                            }}
                        >
                            OUR FEATURES
                        </Typography>
                        <Typography
                            variant="h3"
                            component="h2"
                            sx={{
                                fontWeight: 700,
                                mb: 3,
                                textAlign: 'center',
                                fontSize: { xs: '2rem', sm: '2.4rem' },
                                position: 'relative',
                                display: 'inline-block',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: -10,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: '60px',
                                    height: '4px',
                                    background: '#7e9ecc',
                                    borderRadius: '2px',
                                }
                            }}
                        >
                            主な機能
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                mt: 3,
                                maxWidth: '700px',
                                mx: 'auto',
                                color: 'text.secondary',
                                fontSize: '1.1rem',
                            }}
                        >
                            Room Share Matchingだからこそ実現できるシンプルで使いやすい機能で、
                            理想の住まい探しをサポートします。
                        </Typography>
                    </Box>

                    <Grid container spacing={4} justifyContent="center">
                        {[
                            {
                                icon: <SearchIcon fontSize="large" />,
                                title: '物件検索',
                                description: 'エリア、家賃、設備など様々な条件からルームシェア募集中の物件を簡単に検索できます。',
                                color: '#7e9ecc',
                                gradient: `linear-gradient(135deg, #79a8ed, #6a89cc)`,
                            },
                            {
                                icon: <PeopleIcon fontSize="large" />,
                                title: 'ルームメイト検索',
                                description: '年齢、趣味、生活スタイルなどから相性の良いルームメイトを見つけられます。',
                                color: '#4ECDC4', // ミントグリーン
                                gradient: 'linear-gradient(135deg, #4ECDC4, #2BAE9F)',
                            },
                            {
                                icon: <SecurityIcon fontSize="large" />,
                                title: '安心の仕組み',
                                description: '電話番号認証、メールアドレス認証、同性限定連絡、通報機能、ブロック機能で、安心してやり取りができます。',
                                color: '#FF6B6B', // コーラルピンク
                                gradient: 'linear-gradient(135deg, #FF6B6B, #E84855)',
                            },
                        ].map((feature, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        borderRadius: '16px',
                                        transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                                        boxShadow: '0 6px 20px rgba(0,0,0,0.06)',
                                        border: '1px solid rgba(0,0,0,0.04)',
                                        overflow: 'hidden',
                                        position: 'relative',
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '5px',
                                            background: feature.gradient,
                                        },
                                        '&:hover': {
                                            transform: 'translateY(-10px) scale(1.02)',
                                            boxShadow: '0 16px 30px rgba(0,0,0,0.1)',
                                        },
                                    }}
                                >
                                    <CardContent sx={{ flexGrow: 1, p: 4, pt: 5 }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                mb: 3,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: 80,
                                                    height: 80,
                                                    borderRadius: '18px',
                                                    background: feature.gradient,
                                                    color: '#fff',
                                                    mb: 2,
                                                    boxShadow: `0 8px 20px ${feature.color}40`,
                                                    '& svg': {
                                                        fontSize: '2.2rem',
                                                    }
                                                }}
                                            >
                                                {feature.icon}
                                            </Box>
                                        </Box>
                                        <Typography
                                            variant="h5"
                                            component="h3"
                                            sx={{
                                                fontWeight: 700,
                                                mb: 2,
                                                textAlign: 'center',
                                                fontSize: '1.4rem',
                                                color: 'text.primary',
                                            }}
                                        >
                                            {feature.title}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                textAlign: 'center',
                                                fontSize: '1rem',
                                                lineHeight: 1.7,
                                                color: 'text.secondary',
                                            }}
                                        >
                                            {feature.description}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* メリット紹介 - 洗練されたデザイン */}
            <Box
                sx={{
                    py: { xs: 8, sm: 12 },
                    background: '#fff',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* 装飾要素 - 背景パターン */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        opacity: 0.02,
                        backgroundImage: `
                            linear-gradient(45deg, #7e9ecc 25%, transparent 25%), 
                            linear-gradient(-45deg, #7e9ecc 25%, transparent 25%), 
                            linear-gradient(45deg, transparent 75%, #7e9ecc 75%), 
                            linear-gradient(-45deg, transparent 75%, #7e9ecc 75%)
                        `,
                        backgroundSize: '20px 20px',
                        backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
                    }}
                />

                {/* 波形装飾 - 上部 */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: -1,
                        left: 0,
                        right: 0,
                        height: '80px',
                        background: theme.palette.grey[50],
                        clipPath: 'polygon(100% 0, 0 0, 0 100%, 50% 60%, 100% 100%)',
                    }}
                />

                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ textAlign: 'center', mb: 8 }}>
                        <Typography
                            variant="overline"
                            component="div"
                            sx={{
                                color: '#7e9ecc',
                                fontWeight: 600,
                                mb: 1,
                                letterSpacing: 2,
                                fontSize: '0.9rem',
                            }}
                        >
                            BENEFITS
                        </Typography>
                        <Typography
                            variant="h3"
                            component="h2"
                            sx={{
                                fontWeight: 700,
                                mb: 3,
                                textAlign: 'center',
                                fontSize: { xs: '2rem', sm: '2.4rem' },
                                position: 'relative',
                                display: 'inline-block',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: -10,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: '60px',
                                    height: '4px',
                                    background: '#7e9ecc',
                                    borderRadius: '2px',
                                }
                            }}
                        >
                            ルームシェアが叶える3つの価値
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                mt: 3,
                                maxWidth: '700px',
                                mx: 'auto',
                                color: 'text.secondary',
                                fontSize: '1.1rem',
                            }}
                        >
                            ルームシェアはただの住まい方ではなく、新しいライフスタイルです。
                            より豊かな暮らしのための3つの価値をご紹介します。
                        </Typography>
                    </Box>

                    <Grid container spacing={5}>
                        {[
                            {
                                icon: <AttachMoneyIcon fontSize="large" />,
                                title: '経済的メリット',
                                description: '家賃・光熱費などの生活コストを分担することで、経済的な負担を軽減できます。一人暮らしと比べて20-40%程度の節約が期待できます。',
                                accentColor: '#FF9F1C', // オレンジ
                                benefit: '月々の負担軽減',
                            },
                            {
                                icon: <CelebrationIcon fontSize="large" />,
                                title: '生活の豊かさ',
                                description: '新しい人との出会いや共同生活を通じて、一人暮らしでは得られない経験や視野の広がりが生まれます。異なる価値観や文化との交流も魅力です。',
                                accentColor: '#4ECDC4', // ミントグリーン
                                benefit: '人間関係の広がり',
                            },
                            {
                                icon: <HomeIcon fontSize="large" />,
                                title: '住環境の向上',
                                description: 'コストを分担することで、一人では手が届かない立地や設備の良い物件に住めます。より快適な住環境で理想のライフスタイルを実現できます。',
                                accentColor: '#3D5A80', // ネイビーブルー
                                benefit: '快適な住環境',
                            },
                        ].map((benefit, index) => (
                            <Grid item xs={12} sm={4} key={index}>
                                <Box
                                    sx={{
                                        height: '100%',
                                        background: '#fff',
                                        borderRadius: '16px',
                                        p: 4,
                                        boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                                        transition: 'all 0.3s ease',
                                        border: '1px solid rgba(0,0,0,0.03)',
                                        position: 'relative',
                                        '&:hover': {
                                            transform: 'translateY(-8px)',
                                            boxShadow: '0 15px 35px rgba(0,0,0,0.08)',
                                        },
                                    }}
                                >
                                    {/* アイコン */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 70,
                                            height: 70,
                                            borderRadius: '50%',
                                            background: `${benefit.accentColor}10`,
                                            color: benefit.accentColor,
                                            mb: 3,
                                            position: 'relative',
                                            margin: '0 auto',
                                            '&::after': {
                                                content: '""',
                                                position: 'absolute',
                                                width: '80%',
                                                height: '80%',
                                                borderRadius: '50%',
                                                border: `2px dashed ${benefit.accentColor}40`,
                                                animation: 'rotate 30s linear infinite',
                                            }
                                        }}
                                    >
                                        {benefit.icon}
                                    </Box>

                                    {/* 見出し */}
                                    <Typography
                                        variant="h5"
                                        component="h3"
                                        sx={{
                                            fontWeight: 700,
                                            mb: 1.5,
                                            fontSize: '1.3rem',
                                            color: benefit.accentColor,
                                            textAlign: 'center',
                                        }}
                                    >
                                        {benefit.title}
                                    </Typography>

                                    {/* ハイライト */}
                                    <Box
                                        sx={{
                                            display: 'block',
                                            px: 2,
                                            py: 0.5,
                                            borderRadius: '20px',
                                            background: `${benefit.accentColor}15`,
                                            color: benefit.accentColor,
                                            fontWeight: 600,
                                            fontSize: '0.9rem',
                                            mb: 2,
                                            textAlign: 'center',
                                            mx: 'auto',
                                            width: 'fit-content',
                                        }}
                                    >
                                        {benefit.benefit}
                                    </Box>

                                    {/* 説明文 */}
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontSize: '1rem',
                                            lineHeight: 1.7,
                                            color: 'text.secondary',
                                            textAlign: 'center',
                                        }}
                                    >
                                        {benefit.description}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* サービス開始予告 - 洗練されたデザイン */}
            <Box
                sx={{
                    py: { xs: 8, sm: 10 },
                    position: 'relative',
                    overflow: 'hidden',
                    background: `linear-gradient(135deg, #7e9ecc, #a5c6f7)`,
                }}
            >
                {/* 装飾要素 - 幾何学模様 */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 0,
                        opacity: 0.1,
                        backgroundImage: `
                            linear-gradient(45deg, rgba(255,255,255,0.2) 25%, transparent 25%, transparent 75%, rgba(255,255,255,0.2) 75%),
                            linear-gradient(45deg, rgba(255,255,255,0.2) 25%, transparent 25%, transparent 75%, rgba(255,255,255,0.2) 75%)
                        `,
                        backgroundSize: '60px 60px',
                        backgroundPosition: '0 0, 30px 30px',
                    }}
                />

                {/* 浮遊する円形装飾 */}
                {[...Array(6)].map((_, i) => (
                    <Box
                        key={i}
                        sx={{
                            position: 'absolute',
                            width: 20 + Math.random() * 60,
                            height: 20 + Math.random() * 60,
                            borderRadius: '50%',
                            background: 'rgba(255,255,255,0.1)',
                            top: `${Math.random() * 100}%`,
                            left: `${Math.random() * 100}%`,
                            animation: `float ${10 + Math.random() * 20}s ease-in-out infinite alternate`,
                        }}
                    />
                ))}

                <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
                    <Box
                        sx={{
                            textAlign: 'center',
                            background: 'rgba(255,255,255,0.1)',
                            borderRadius: '24px',
                            p: { xs: 4, sm: 6 },
                            backdropFilter: 'blur(10px)',
                            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            position: 'relative',
                            overflow: 'hidden',
                        }}
                    >
                        {/* 装飾ハイライト */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: '-50%',
                                width: '200%',
                                height: '100%',
                                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                                animation: 'shimmer 5s infinite',
                                '@keyframes shimmer': {
                                    '0%': { transform: 'translateX(-100%)' },
                                    '100%': { transform: 'translateX(100%)' },
                                }
                            }}
                        />

                        <Typography
                            variant="h3"
                            component="h2"
                            sx={{
                                fontWeight: 800,
                                mb: 3,
                                fontSize: { xs: '2rem', sm: '2.6rem' },
                                color: '#fff',
                                textShadow: '0 2px 10px rgba(0,0,0,0.1)',
                            }}
                        >
                            今すぐ始めましょう
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                mb: 5,
                                mt: 3,
                                fontSize: '1.15rem',
                                maxWidth: '700px',
                                mx: 'auto',
                                color: 'rgba(255, 255, 255, 0.9)',
                                lineHeight: 1.8,
                            }}
                        >
                            理想のルームシェア生活はここから始まります。
                            まずはアカウント登録から、あなたにぴったりの物件やルームメイトを見つけましょう。
                        </Typography>

                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: 3,
                            flexWrap: 'wrap'
                        }}>
                            <Button
                                variant="contained"
                                size="large"
                                onClick={() => navigate('/register')}
                                sx={{
                                    borderRadius: '30px',
                                    px: 5,
                                    py: 1.8,
                                    fontSize: '1.1rem',
                                    fontWeight: 700,
                                    bgcolor: '#fff',
                                    color: '#7e9ecc',
                                    '&:hover': {
                                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                                        transform: 'translateY(-3px)',
                                        boxShadow: '0 10px 20px rgba(0,0,0,0.15)'
                                    },
                                    transition: 'all 0.3s ease',
                                    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                                }}
                            >
                                アカウント登録
                            </Button>
                            <Button
                                variant="outlined"
                                size="large"
                                onClick={() => navigate('/guide')}
                                sx={{
                                    borderRadius: '30px',
                                    px: 5,
                                    py: 1.8,
                                    fontSize: '1.1rem',
                                    fontWeight: 600,
                                    borderColor: 'rgba(255, 255, 255, 0.5)',
                                    borderWidth: 2,
                                    color: '#fff',
                                    '&:hover': {
                                        borderColor: '#fff',
                                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                                        transform: 'translateY(-3px)',
                                    },
                                    transition: 'all 0.3s ease',
                                }}
                            >
                                詳しくはこちら
                            </Button>
                        </Box>
                    </Box>
                </Container>
            </Box>

            {/* FAQ - 洗練されたデザイン */}
            <Box
                sx={{
                    py: { xs: 8, sm: 12 },
                    background: '#fff',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* 装飾要素 - 背景 */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        opacity: 0.02,
                        backgroundImage: `url('/images/pattern-bg.png')`,
                        backgroundSize: '300px 300px',
                    }}
                />

                {/* 装飾要素 - 左下の円 */}
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: -100,
                        left: -100,
                        width: 300,
                        height: 300,
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, #b8daff10, #7e9ecc05)`,
                        zIndex: 0,
                    }}
                />

                {/* 装飾要素 - 右上の円 */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: -50,
                        right: -50,
                        width: 200,
                        height: 200,
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, #b8daff05, #7e9ecc10)`,
                        zIndex: 0,
                    }}
                />

                <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ textAlign: 'center', mb: 7 }}>
                        <Typography
                            variant="overline"
                            component="div"
                            sx={{
                                color: '#7e9ecc',
                                fontWeight: 600,
                                mb: 1,
                                letterSpacing: 2,
                                fontSize: '0.9rem',
                            }}
                        >
                            FAQ
                        </Typography>
                        <Typography
                            variant="h3"
                            component="h2"
                            sx={{
                                fontWeight: 700,
                                mb: 3,
                                fontSize: { xs: '2rem', sm: '2.4rem' },
                                position: 'relative',
                                display: 'inline-block',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: -10,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: '60px',
                                    height: '4px',
                                    background: '#7e9ecc',
                                    borderRadius: '2px',
                                }
                            }}
                        >
                            よくある質問
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                mt: 3,
                                mb: 5,
                                maxWidth: '700px',
                                mx: 'auto',
                                color: 'text.secondary',
                                fontSize: '1.1rem',
                            }}
                        >
                            Room Share Matchingに関するよくある質問にお答えします。
                            さらに詳しい情報は<Box component="span" sx={{ color: '#7e9ecc', fontWeight: 600, cursor: 'pointer' }} onClick={() => navigate('/guide')}>ガイドページ</Box>をご覧ください。
                        </Typography>
                    </Box>

                    <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
                        {[
                            {
                                id: 'faq1',
                                question: 'Room Share Matchingとは？',
                                answer: 'Room Share Matchingは、ルームシェア物件を提供したい方とルームメイトを探している方をつなぐプラットフォームです。物件検索、ルームメイト検索、メッセージ機能などを通じて理想の住まい探しをサポートします。',
                            },
                            {
                                id: 'faq2',
                                question: '利用料金はどのようになっていますか？',
                                answer: '基本的な物件検索やルームメイト検索は無料でご利用いただけます。一部の機能（メッセージの送信制限解除など）は有料プランでご利用いただけます。詳細は今後公開されます。',
                            },
                            {
                                id: 'faq3',
                                question: 'どのようにして安全性を確保していますか？',
                                answer: '電話番号認証、メールアドレス認証、同性限定連絡、通報機能、ブロック機能などを通じて、安心なプラットフォームの運営に努めています。また、利用規約に違反するユーザーには厳正に対処します。',
                            },
                            {
                                id: 'faq4',
                                question: 'どのような物件が掲載されていますか？',
                                answer: '一軒家のルームシェアからマンションのシェアルーム、ゲストハウスまで、様々なタイプの物件が掲載されています。家賃や立地、設備などの詳細条件で絞り込み検索も可能です。',
                            },
                        ].map((faq) => (
                            <Accordion
                                key={faq.id}
                                expanded={expandedFaq === faq.id}
                                onChange={handleFaqChange(faq.id)}
                                sx={{
                                    mb: 2.5,
                                    boxShadow: 'none',
                                    border: '1px solid',
                                    borderColor: expandedFaq === faq.id ? '#7e9ecc' : theme.palette.grey[200],
                                    borderRadius: '16px !important',
                                    '&:before': {
                                        display: 'none',
                                    },
                                    overflow: 'hidden',
                                    transition: 'all 0.3s ease',
                                    background: expandedFaq === faq.id ? `#7e9ecc05` : '#fff',
                                    '&:hover': {
                                        borderColor: '#7e9ecc',
                                        background: expandedFaq === faq.id ? `#7e9ecc05` : `#7e9ecc03`,
                                    },
                                    position: 'relative',
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        '& .MuiAccordionSummary-content': {
                                            margin: '16px 0',
                                            alignItems: 'center',
                                        },
                                        '& .MuiSvgIcon-root': {
                                            color: '#7e9ecc',
                                            transition: 'transform 0.3s ease',
                                            transform: expandedFaq === faq.id ? 'rotate(180deg)' : 'rotate(0)',
                                            fontSize: '1.5rem',
                                        }
                                    }}
                                >
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: '1.15rem',
                                                color: expandedFaq === faq.id ? '#7e9ecc' : 'text.primary',
                                            }}
                                        >
                                            {faq.question}
                                        </Typography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: 'text.secondary',
                                            mb: 1.5,
                                            lineHeight: 1.8,
                                            position: 'relative',
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                left: -10,
                                                top: 0,
                                                bottom: 0,
                                                width: 2,
                                                background: `#7e9ecc30`,
                                                borderRadius: '2px',
                                            }
                                        }}
                                    >
                                        {faq.answer}
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Box>
                </Container>
            </Box>

            {/* CTA - 洗練されたデザイン */}
            <Box
                sx={{
                    py: { xs: 10, sm: 12 },
                    background: `linear-gradient(135deg, #7e9ecc, #a5c6f7)`,
                    color: '#fff',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* 装飾要素 - 波形背景 */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        opacity: 0.05,
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='20' viewBox='0 0 100 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M21.184 20c.357-.13.72-.264 1.088-.402l1.768-.661C33.64 15.347 39.647 14 50 14c10.271 0 15.362 1.222 24.629 4.928.955.383 1.869.74 2.75 1.072h6.225c-2.51-.73-5.139-1.691-8.233-2.928C65.888 13.278 60.562 12 50 12c-10.626 0-16.855 1.397-26.66 5.063l-1.767.662c-2.475.923-4.66 1.674-6.724 2.275h6.335zm0-20C13.258 2.892 8.077 4 0 4V2c5.744 0 9.951-.574 14.85-2h6.334zM77.38 0C85.239 2.966 90.502 4 100 4V2c-6.842 0-11.386-.542-16.396-2h-6.225zM0 14c8.44 0 13.718-1.21 22.272-4.402l1.768-.661C33.64 5.347 39.647 4 50 4c10.271 0 15.362 1.222 24.629 4.928C84.112 12.722 89.438 14 100 14v-2c-10.271 0-15.362-1.222-24.629-4.928C65.888 3.278 60.562 2 50 2 39.374 2 33.145 3.397 23.34 7.063l-1.767.662C13.223 10.84 8.163 12 0 12v2z' fill='%23ffffff' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`,
                        backgroundSize: '100px 20px',
                        animation: 'wave 20s linear infinite',
                        '@keyframes wave': {
                            '0%': { backgroundPosition: '0 0' },
                            '100%': { backgroundPosition: '1000px 0' },
                        }
                    }}
                />

                {/* 装飾ドット */}
                {[...Array(20)].map((_, i) => (
                    <Box
                        key={i}
                        sx={{
                            position: 'absolute',
                            width: 2 + Math.random() * 4,
                            height: 2 + Math.random() * 4,
                            borderRadius: '50%',
                            background: 'rgba(255,255,255,0.4)',
                            top: `${Math.random() * 100}%`,
                            left: `${Math.random() * 100}%`,
                            animation: `twinkle ${2 + Math.random() * 5}s ease-in-out infinite alternate`,
                            '@keyframes twinkle': {
                                '0%': { opacity: 0.2, transform: 'scale(1)' },
                                '100%': { opacity: 0.8, transform: 'scale(1.5)' },
                            }
                        }}
                    />
                ))}

                <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{
                        textAlign: 'center',
                        position: 'relative',
                    }}>
                        {/* 上部装飾要素 */}
                        <Box sx={{
                            mb: 3,
                            display: 'flex',
                            justifyContent: 'center'
                        }}>
                            <Box sx={{
                                width: '80px',
                                height: '4px',
                                background: 'rgba(255,255,255,0.3)',
                                borderRadius: '2px',
                                position: 'relative',
                                '&::before, &::after': {
                                    content: '""',
                                    position: 'absolute',
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '50%',
                                    background: 'rgba(255,255,255,0.6)',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                },
                                '&::before': {
                                    left: -5,
                                },
                                '&::after': {
                                    right: -5,
                                }
                            }} />
                        </Box>

                        <Typography
                            variant="h3"
                            component="h2"
                            sx={{
                                fontWeight: 800,
                                mb: 3,
                                fontSize: { xs: '2.2rem', sm: '2.8rem' },
                                color: '#fff',
                                textShadow: '0 2px 15px rgba(0,0,0,0.1)',
                                lineHeight: 1.3,
                                position: 'relative',
                                display: 'inline-block',
                            }}
                        >
                            新しい暮らしの第一歩を踏み出そう
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                mb: 5,
                                mt: 3,
                                fontSize: '1.15rem',
                                maxWidth: '700px',
                                mx: 'auto',
                                color: 'rgba(255, 255, 255, 0.9)',
                                lineHeight: 1.8,
                            }}
                        >
                            理想のルームシェア生活はここから始まります。
                            まずはアカウント登録から、あなたにぴったりの物件やルームメイトを見つけましょう。
                            シンプルなステップで、新しい生活様式への扉が開きます。
                        </Typography>

                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: 3,
                            flexWrap: 'wrap'
                        }}>
                            <Button
                                variant="contained"
                                size="large"
                                onClick={() => navigate('/register')}
                                sx={{
                                    borderRadius: '30px',
                                    px: 5,
                                    py: 1.8,
                                    fontSize: '1.1rem',
                                    fontWeight: 700,
                                    bgcolor: '#fff',
                                    color: '#7e9ecc',
                                    '&:hover': {
                                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                                        transform: 'translateY(-3px)',
                                        boxShadow: '0 10px 20px rgba(0,0,0,0.15)'
                                    },
                                    transition: 'all 0.3s ease',
                                    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                                }}
                            >
                                アカウント登録
                            </Button>
                            <Button
                                variant="outlined"
                                size="large"
                                onClick={() => navigate('/guide')}
                                sx={{
                                    borderRadius: '30px',
                                    px: 5,
                                    py: 1.8,
                                    fontSize: '1.1rem',
                                    fontWeight: 600,
                                    borderColor: 'rgba(255, 255, 255, 0.5)',
                                    borderWidth: 2,
                                    color: '#fff',
                                    '&:hover': {
                                        borderColor: '#fff',
                                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                                        transform: 'translateY(-3px)',
                                    },
                                    transition: 'all 0.3s ease',
                                }}
                            >
                                詳しくはこちら
                            </Button>
                        </Box>
                    </Box>
                </Container>
            </Box>
        </>
    );
}

export default Home; 